import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';

import compose from 'helpers/compose';
import Background from 'components/Layout/Background';
import ControlButtons from 'components/ControlButtons';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Info from 'components/Tooltip/Info';

import { WorkflowQuestionnaireStore } from 'stores/workflowStores/workflowQuestionnaireStore/newWorkflowQuestionnaireStore';
import EvaluationCategory from 'apps/Pdf/templates/InvestmentQuestionnaire/components/EvaluationCategory';

interface Props {
  store: WorkflowQuestionnaireStore;
  goFwd?: () => void;
  detailMode?: boolean;
  readOnly?: boolean;
  userMode?: boolean;
  editMode?: boolean;
  takeAgainDisabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(2),
      '& > div': {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
      '& .title': {
        paddingLeft: theme.spacing(1),
      },
      '@media (max-width: 400px)': {
        '& .minLabel, & .maxLabel, & .gaugeWrapper': { maxWidth: '33.3334%', flexBasis: '33.3334%' },
      },
    },
    padding: {
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        '& .minLabel': { paddingRight: theme.spacing(1) },
        '& .maxLabel': { paddingLeft: theme.spacing(1) },
      },
    },
    esg: {
      padding: theme.spacing(2),
      width: '92%',
    },
    title: {
      fontSize: '20pt',
      fontWeight: 800
    }
  }),
);

const Evaluation: React.FC<Props> = ({ store, goFwd, detailMode, readOnly, userMode, takeAgainDisabled }) => {
  const { t } = useTranslation();
  const result = store.resultData!.result;
  const classes = useStyles();
  const [profileChangeOpen, setProfileChangeOpen] = useState(false);
  const [linkedInfoOpen, setLinkedInfoOpen] = useState(false);
  const [changeChecked, setChangeChecked] = useState(false);
  const [linkedChecked, setLinkedChecked] = useState(false);

  const handleNextStep = () => {
    setProfileChangeOpen(false);
    setLinkedInfoOpen(false);

    if (!changeChecked && store.profileChange !== 0) {
      setLinkedChecked(true);
      setChangeChecked(true);
      setProfileChangeOpen(true);
      return false;
    }
    if (!linkedChecked && result.linked) {
      setLinkedChecked(true);
      setLinkedInfoOpen(true);
      return false;
    }
    if (detailMode && !!goFwd) {
      goFwd();
    } else {
      store.handleNextStep();
    }
  };

  return (
    <>
      <Container maxWidth="md" className={`${classes.root} ${classes.padding}`}>
        <Background className={`${classes.padding}`}>
          <Typography variant="h1" align="center" className={classes.title}>
            {t('accountInvestment_evaluation')}
            {result?.linked && <Info content={t('accountInvestment_evaluation_linked')} heading />}
          </Typography>

          <EvaluationCategory
            risk={result.categoryProfiles.RISK_TOLERANCE.toString(10)}
            category={t('investmentQuestionnaire_riskTolerance')}
            minValueLabel={t('investmentQuestionnaire_careful')}
            maxValueLabel={t('investmentQuestionnaire_brave')}
            fe
          />
          <EvaluationCategory
            risk={result.categoryProfiles.EXPERIENCE.toString(10)}
            category={t(
              result.categoryProfiles.KNOWLEDGE
                ? 'investmentQuestionnaire_experience'
                : 'investmentQuestionnaire_expAndKnowledge',
            )}
            minValueLabel={t('investmentQuestionnaire_expLow')}
            maxValueLabel={t('investmentQuestionnaire_expHigh')}
            fe
          />
          {result.categoryProfiles.KNOWLEDGE !== undefined && (
            <EvaluationCategory
              risk={result.categoryProfiles.KNOWLEDGE.toString(10)}
              category={t('investmentQuestionnaire_knowledge')}
              minValueLabel={t('investmentQuestionnaire_expLow')}
              maxValueLabel={t('investmentQuestionnaire_expHigh')}
              fe
            />
          )}
          <EvaluationCategory
            risk={result.categoryProfiles.BACKGROUND.toString(10)}
            category={t('investmentQuestionnaire_background')}
            minValueLabel={t('investmentQuestionnaire_unstable')}
            maxValueLabel={t('investmentQuestionnaire_stable')}
            fe
          />
          <EvaluationCategory
            risk={result.categoryProfiles.HORIZON.toString(10)}
            category={t('investmentQuestionnaire_horizon')}
            minValueLabel={t('investmentQuestionnaire_shortTerm')}
            maxValueLabel={t('investmentQuestionnaire_longTerm')}
            fe
          />

          {result.esg !== undefined && (
            <EvaluationCategory
              category={t('investmentQuestionnaire_esg_title')}
              esg={t(`investmentQuestionnaire_esg_${result.esg}`)}
              fe
            />
          )}

          <EvaluationCategory
            risk={result.profile.toString(10)}
            category={t('investmentQuestionnaire_profile')}
            minValueLabel={t('investmentQuestionnaire_conservative')}
            maxValueLabel={t('investmentQuestionnaire_aggressive')}
            total
            fe
          />
        </Background>
      </Container>

      {userMode && (
        <Container maxWidth="md" className={`${classes.root} ${classes.padding}`}>
          <Background>
            <Typography variant="h1" align="center">
              {t('accountInvestment_evaluation_title')}
            </Typography>

            <Typography>{t('accountInvestment_evaluation_info1')}</Typography>
            <Typography>{t('accountInvestment_evaluation_info2')}</Typography>
          </Background>
        </Container>
      )}

      <ConfirmDialog title={null} isOpen={profileChangeOpen} confirm={handleNextStep} disableClose>
        {t('accountInvestment_evaluation_profileChange')}
      </ConfirmDialog>

      <ConfirmDialog title={null} isOpen={linkedInfoOpen} confirm={handleNextStep} disableClose>
        {t('accountInvestment_evaluation_linkedInfo')}
      </ConfirmDialog>

      {!readOnly && (
        <ControlButtons
          isSubmitting={false}
          onNext={handleNextStep}
          onBack={store.handleTakeAgain}
          nextText={t('accountStepper_planProposal')}
          backText={t('investmentQuestionnaire_takeAgain')}
          backDisabled={takeAgainDisabled}
          nextButtonId="submit-form-button"
          backButtonId="questionnaire-again-button"
        />
      )}
    </>
  );
};

export default compose(inject('store'), observer)(Evaluation);
